import Vue from 'vue';
import axios from '@/plugins/axios';
import qs from 'qs';

const state = {
    downloads: [],
    modalOpen: false,
};

const mutations = {
    addDownload(state, download) {
        state.downloads.push(download);
    },
    updateDownload(state, { id, updates }) {
        const index = state.downloads.findIndex(d => d.id === id);
        if (index !== -1) {
            // Use Vue.set to ensure reactivity
            Object.keys(updates).forEach(key => {
                Vue.set(state.downloads[index], key, updates[key]);
            });
        }
    },
    removeDownload(state, id) {
        state.downloads = state.downloads.filter(d => d.id !== id);
    },
    setModalOpen(state, open) {
        state.modalOpen = open;
    },
    clearCompletedDownloads(state) {
        state.downloads = state.downloads.filter(d => d.status !== 'completed' && d.status !== 'error');
    },
};

const actions = {
    addDownload({ commit }, download) {
        commit('addDownload', download);
    },
    updateDownload({ commit }, { id, updates }) {
        commit('updateDownload', { id, updates });
    },
    removeDownload({ commit }, id) {
        commit('removeDownload', id);
    },
    setModalOpen({ commit }, open) {
        commit('setModalOpen', open);
    },
    clearCompletedDownloads({ commit }) {
        commit('clearCompletedDownloads');
    },
    cancelDownload({ commit, state }, id) {
        const download = state.downloads.find(d => d.id === id);
        if (download && download.cancelToken) {
            download.cancelToken.cancel('Download cancelled by user');
            commit('updateDownload', {
                id,
                updates: { status: 'cancelled' },
            });
            // Remove cancelled download after a short delay
            setTimeout(() => {
                commit('removeDownload', id);
            }, 1000);
        }
    },
    async startDownload({ commit, dispatch }, { blobOrUrlOrConfig, filename = null }) {
        const downloadId = `download_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        let downloadFilename = filename;
        const extractFilename = (url) => {
            try {
                const urlObj = new URL(url);
                const pathname = urlObj.pathname;
                const filename = pathname.split('/').pop();
                return filename.split('?')[0] || null;
            } catch (e) {
                return null;
            }
        };

        try {
            let blob;
            let cancelToken = null;

            if (blobOrUrlOrConfig instanceof Blob) {
                blob = blobOrUrlOrConfig;
                downloadFilename = downloadFilename || 'download';
                // For Blob, mark as completed immediately
                commit('addDownload', {
                    id: downloadId,
                    filename: downloadFilename,
                    status: 'completed',
                    progress: 100,
                });
            } else if (typeof blobOrUrlOrConfig === 'string') {
                // If it's a URL string, fetch it first
                downloadFilename = downloadFilename || extractFilename(blobOrUrlOrConfig) || 'download';
                
                cancelToken = axios.CancelToken.source();
                
                // Add download to store
                commit('addDownload', {
                    id: downloadId,
                    filename: downloadFilename,
                    status: 'downloading',
                    progress: null,
                    loaded: null,
                    total: null,
                    cancelToken,
                });
                
                const response = await axios.get(blobOrUrlOrConfig, {
                    responseType: 'blob',
                    cancelToken: cancelToken.token,
                    onDownloadProgress: (progressEvent) => {
                        const updates = {
                            loaded: progressEvent.loaded,
                            total: progressEvent.total,
                        };
                        if (progressEvent.total) {
                            updates.progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        }
                        commit('updateDownload', {
                            id: downloadId,
                            updates,
                        });
                    },
                });
                blob = response.data;
            } else if (typeof blobOrUrlOrConfig === 'object' && blobOrUrlOrConfig !== null) {
                // If it's a config object with url and axios options
                const { url, ...axiosConfig } = blobOrUrlOrConfig;
                downloadFilename = downloadFilename || extractFilename(url) || 'download';
                
                cancelToken = axios.CancelToken.source();
                
                // Add download to store
                commit('addDownload', {
                    id: downloadId,
                    filename: downloadFilename,
                    status: 'downloading',
                    progress: null,
                    loaded: null,
                    total: null,
                    cancelToken,
                });
                
                const response = await axios.get(url, {
                    ...axiosConfig,
                    paramsSerializer: axiosConfig.paramsSerializer || ((params) => qs.stringify(params, { arrayFormat: 'comma' })),
                    responseType: 'blob',
                    cancelToken: cancelToken.token,
                    onDownloadProgress: (progressEvent) => {
                        const updates = {
                            loaded: progressEvent.loaded,
                            total: progressEvent.total,
                        };
                        if (progressEvent.total) {
                            updates.progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        }
                        commit('updateDownload', {
                            id: downloadId,
                            updates,
                        });
                    },
                });
                blob = response.data;
            } else {
                throw new Error('downloadFile expects a Blob, URL string, or config object with url property');
            }
            
            // Update status to completed
            commit('updateDownload', {
                id: downloadId,
                updates: { status: 'completed', progress: 100 },
            });
            
            // Trigger file download
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            if (downloadFilename) {
                link.setAttribute('download', downloadFilename);
            }
            
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (xhr_error) {
            // If the request was cancelled, remove from store and don't throw
            if (axios.isCancel && axios.isCancel(xhr_error)) {
                commit('removeDownload', downloadId);
                return;
            }
            
            // Update status to error
            const errorMessage = xhr_error.response?.data?.message || xhr_error.message || 'Download error';
            commit('updateDownload', {
                id: downloadId,
                updates: { status: 'error', error: errorMessage },
            });
            
            // Re-throw the error so the caller can handle it
            throw xhr_error;
        }
    },
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
};

